/**
 * Нөөцийн шатлал — салбар / агуулах → байршил → гутлын тоо
 * (захиалагчийн цэгцлэл №4, №13, 2026-10-05).
 *
 * Өмнө нь Статистикт хавтгай хүснэгт («Байршил · Салбар · Зарагдах
 * эсэх · Гутал») байж, нэг салбарын байршлууд тоогоор эрэмбэлэгдээд
 * тарж байв. Одоо нэгж бүр өөрийн байршлуудтайгаа нэг блокт.
 *
 * ⚠️ Тоо бүр «Бүх гутал» руу холбоос — `status=all`: тайлангийн тоо нь
 *    төлөв харгалзахгүй (байршилд биетээр байгаа) тул шүүлт нь ч мөн
 *    адил байх ёстой, эс тэгвээс дарахад өөр тоо гарна.
 */

import { formatMntShort, formatQty } from '@boots/domain';
import { Link } from 'react-router-dom';
import { LOCATION_TYPE_LABEL, type StockLocations } from '../../lib/types';
import { Empty } from '../ui';
import { Meter } from '../meter';

type Unit = StockLocations['units'][number];

const GROUPS: { type: Unit['type']; label: string }[] = [
  { type: 'RETAIL', label: 'Салбар' },
  { type: 'WAREHOUSE', label: 'Агуулах' },
];

export function LocationTree({ units, showCost }: { units: Unit[]; showCost: boolean }) {
  if (units.length === 0) return <Empty>Салбар, агуулах бүртгэгдээгүй</Empty>;

  return (
    <div className="flex flex-col gap-4">
      {GROUPS.map((g) => {
        const list = units.filter((u) => u.type === g.type);
        if (list.length === 0) return null;
        return (
          <div key={g.type} className="flex flex-col gap-2">
            <span className="text-label tracking-wide text-ink-3 uppercase">
              {g.label} · {formatQty(list.length)}
            </span>
            {list.map((u) => <UnitBlock key={u.id} unit={u} showCost={showCost} />)}
          </div>
        );
      })}
    </div>
  );
}

function UnitBlock({ unit: u, showCost }: { unit: Unit; showCost: boolean }) {
  return (
    <section className="overflow-hidden rounded-well border border-hair">
      <Link
        to={`/items?storeId=${u.id}&status=all`}
        className="flex flex-wrap items-baseline gap-x-4 gap-y-1 bg-well px-3.5 py-2.5 transition hover:bg-row-hover"
      >
        <span className="flex items-baseline gap-2">
          <span className="text-[14px] font-semibold">{u.name}</span>
          <span className="num text-label text-ink-3">{u.code}</span>
        </span>
        <span className="ml-auto flex flex-wrap items-baseline gap-x-4 gap-y-1 text-meta text-ink-2">
          <span><span className="num font-semibold text-ink">{formatQty(u.items)}</span> гутал</span>
          <span><span className="num text-ink">{formatQty(u.sellable)}</span> бэлэн</span>
          {u.unlabeled > 0 ? (
            <span className="text-alert-ink"><span className="num">{formatQty(u.unlabeled)}</span> шошгогүй</span>
          ) : null}
          {showCost ? <span className="num">{formatMntShort(u.landedCostMnt)}</span> : null}
        </span>
      </Link>

      <ul className="divide-y divide-hair">
        {u.locations.map((l) => (
          <li key={l.id}>
            <Link
              to={`/items?locationId=${l.id}&status=all`}
              className={`grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-1 px-3.5 py-2
                transition hover:bg-row-hover sm:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_auto] ${
                  l.items === 0 ? 'text-ink-3' : ''
                }`}
            >
              <span className="flex min-w-0 flex-col">
                <span className="truncate text-[13px]">{l.name}</span>
                <span className="text-micro text-ink-3">
                  {LOCATION_TYPE_LABEL[l.type] ?? l.type}
                  {' · '}
                  <span className={l.isSellable ? 'text-accent-700' : ''}>
                    {l.isSellable ? 'зарагдана' : 'зарагдахгүй'}
                  </span>
                </span>
              </span>
              <Meter
                value={u.items > 0 ? (l.items / u.items) * 100 : 0}
                size="sm"
                className="hidden sm:block"
              />
              <span className="num text-right text-[13px]">{formatQty(l.items)}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
